(() => {
  if (window.dkbEpicureanIntegrationLoaded) return;
  window.dkbEpicureanIntegrationLoaded = true;

  const SDK_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
  const STOREFRONT_API_VERSION = '2026-04';

  function loadSdk() {
    if (window.ShopifyBuy && window.ShopifyBuy.UI) {
      return Promise.resolve(window.ShopifyBuy);
    }

    if (window.dkbEpicureanSdkPromise) {
      return window.dkbEpicureanSdkPromise;
    }

    window.dkbEpicureanSdkPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${SDK_URL}"]`);
      const script = existingScript || document.createElement('script');

      script.addEventListener('load', () => resolve(window.ShopifyBuy), { once: true });
      script.addEventListener('error', reject, { once: true });

      if (!existingScript) {
        script.async = true;
        script.src = SDK_URL;
        document.head.appendChild(script);
      }
    });

    return window.dkbEpicureanSdkPromise;
  }

  function componentOptions() {
    const red = '#c10016';
    const redHover = '#a90013';

    return {
      product: {
        styles: {
          product: {
            'max-width': '100%',
            'margin-bottom': '0',
            'text-align': 'left'
          },
          price: {
            'font-size': '20px',
            'font-weight': '700',
            color: '#232323'
          },
          select: {
            'min-height': '48px',
            'border-color': 'rgba(35, 35, 35, 0.28)',
            'border-radius': '8px'
          },
          quantityInput: {
            'min-height': '48px',
            'border-radius': '8px'
          },
          button: {
            'min-height': '48px',
            'padding-left': '24px',
            'padding-right': '24px',
            'background-color': red,
            'border-radius': '999px',
            'font-size': '16px',
            'font-weight': '700',
            ':hover': {
              'background-color': redHover
            },
            ':focus': {
              'background-color': redHover
            }
          }
        },
        contents: {
          img: false,
          title: false,
          description: false,
          button: false,
          buttonWithQuantity: true
        },
        text: {
          button: 'Add to cart',
          outOfStock: 'Out of stock'
        }
      },
      cart: {
        styles: {
          button: {
            'background-color': red,
            'border-radius': '999px',
            'font-size': '16px',
            ':hover': {
              'background-color': redHover
            },
            ':focus': {
              'background-color': redHover
            }
          }
        },
        contents: {
          note: true
        },
        text: {
          button: 'Continue to secure checkout',
          noteDescription: 'Add a gift note',
          notice: 'Your purchase, payment, shipping, and fulfillment are handled by The Epicurean Trader, an independent third-party retailer.'
        }
      },
      toggle: {
        styles: {
          toggle: {
            'background-color': red,
            ':hover': {
              'background-color': redHover
            },
            ':focus': {
              'background-color': redHover
            }
          }
        }
      }
    };
  }

  async function updateStockStatus(section, domain, storefrontAccessToken, productId) {
    const status = section.querySelector('[data-epicurean-stock-status]');
    const statusText = section.querySelector('[data-epicurean-stock-text]');
    if (!status || !statusText) return;

    const query = `
      query ProductAvailability($id: ID!) {
        product(id: $id) {
          availableForSale
          title
          handle
          description
          featuredImage {
            url
          }
          variants(first: 100) {
            nodes {
              availableForSale
              price {
                amount
                currencyCode
              }
            }
          }
        }
      }
    `;
    const response = await fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({
        query,
        variables: {
          id: `gid://shopify/Product/${productId}`
        }
      })
    });
    const result = await response.json();

    if (!response.ok || result.errors || !result.data?.product) {
      throw new Error('Product availability could not be loaded.');
    }

    const inStock = result.data.product.availableForSale;
    status.classList.toggle('epicurean-purchase__stock-status--in-stock', inStock);
    status.classList.toggle('epicurean-purchase__stock-status--out-of-stock', !inStock);
    statusText.textContent = inStock ? 'In stock' : 'Out of stock';
    return result.data.product;
  }

  function updateProductSchema(section, product) {
    const script = section.previousElementSibling?.matches('[data-epicurean-product-schema]')
      ? section.previousElementSibling
      : document.querySelector('[data-epicurean-product-schema]');
    if (!script || !product) return;

    const schema = JSON.parse(script.textContent);
    const retailerUrl = `https://theepicureantrader.com/products/${product.handle}`;
    schema.name = product.title || schema.name;
    schema.description = product.description || schema.description;
    if (product.featuredImage?.url) schema.image = [product.featuredImage.url];
    schema.offers = product.variants.nodes.map((variant) => ({
      '@type': 'Offer',
      url: retailerUrl,
      price: variant.price.amount,
      priceCurrency: variant.price.currencyCode,
      availability: variant.availableForSale
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: 'The Epicurean Trader',
        url: 'https://theepicureantrader.com/'
      }
    }));
    script.textContent = JSON.stringify(schema);
  }

  async function mount(section) {
    if (section.dataset.epicureanMounted === 'true') return;

    const productId = section.dataset.productId;
    const domain = section.dataset.storeDomain;
    const storefrontAccessToken = section.dataset.storefrontToken;
    const node = section.querySelector('[data-epicurean-product-node]');
    const error = section.querySelector('[data-epicurean-error]');

    if (!productId || !domain || !storefrontAccessToken || !node) return;
    section.dataset.epicureanMounted = 'true';

    try {
      const ShopifyBuy = await loadSdk();
      const client = ShopifyBuy.buildClient({ domain, storefrontAccessToken });
      const ui = await ShopifyBuy.UI.onReady(client);

      updateStockStatus(section, domain, storefrontAccessToken, productId)
        .then((product) => updateProductSchema(section, product))
        .catch(() => {
          const statusText = section.querySelector('[data-epicurean-stock-text]');
          if (statusText) statusText.textContent = 'Stock unavailable';
        });

      ui.createComponent('product', {
        id: productId,
        node,
        moneyFormat: '${{amount}}',
        options: componentOptions()
      });
    } catch (exception) {
      section.dataset.epicureanMounted = 'false';
      if (error) error.hidden = false;
      console.error('Epicurean purchase module failed to load.', exception);
    }
  }

  function mountAll(root = document) {
    root.querySelectorAll('[data-epicurean-purchase]').forEach(mount);
  }

  function formatMoney(money) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: money.currencyCode
    }).format(Number(money.amount));
  }

  async function fetchGridProducts(domain, storefrontAccessToken, productIds) {
    const query = `
      query GridProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    `;
    const response = await fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: JSON.stringify({
        query,
        variables: {
          ids: productIds.map((productId) => `gid://shopify/Product/${productId}`)
        }
      })
    });
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error('Grid product data could not be loaded.');
    }

    return new Map(
      result.data.nodes
        .filter(Boolean)
        .map((product) => [product.id.split('/').pop(), product])
    );
  }

  function updateGridCard(card, product) {
    const price = card.querySelector('[data-epicurean-grid-price]');
    const priceWrapper = price?.closest('.price');
    const stockBadge = card.querySelector('[data-epicurean-grid-stock]');

    if (!price || !stockBadge || !product?.priceRange?.minVariantPrice) {
      throw new Error('Grid product data was incomplete.');
    }

    price.textContent = formatMoney(product.priceRange.minVariantPrice);
    if (priceWrapper) priceWrapper.setAttribute('aria-busy', 'false');

    stockBadge.textContent = product.availableForSale ? '' : 'Sold out';
    stockBadge.hidden = product.availableForSale;
    stockBadge.style.display = product.availableForSale ? 'none' : '';
    card.dataset.epicureanGridState = product.availableForSale ? 'available' : 'sold-out';
  }

  function markGridCardUnavailable(card) {
    const price = card.querySelector('[data-epicurean-grid-price]');
    const priceWrapper = price?.closest('.price');
    const stockBadge = card.querySelector('[data-epicurean-grid-stock]');

    if (price) price.textContent = '';
    if (priceWrapper) priceWrapper.setAttribute('aria-busy', 'false');
    if (stockBadge) {
      stockBadge.textContent = 'Stock unavailable';
      stockBadge.hidden = false;
      stockBadge.style.display = '';
    }
    card.dataset.epicureanGridState = 'unavailable';
  }

  let gridUpdateQueued = false;

  function queueGridUpdate() {
    if (gridUpdateQueued) return;
    gridUpdateQueued = true;

    queueMicrotask(async () => {
      gridUpdateQueued = false;
      const cards = Array.from(
        document.querySelectorAll('[data-epicurean-grid-card]:not([data-epicurean-grid-state])')
      );
      const groups = new Map();

      cards.forEach((card) => {
        const grid = card.closest('[data-epicurean-grid]');
        const domain = grid?.dataset.storeDomain;
        const storefrontAccessToken = grid?.dataset.storefrontToken;
        const productId = card.dataset.productId;

        if (!domain || !storefrontAccessToken || !productId) {
          markGridCardUnavailable(card);
          return;
        }

        card.dataset.epicureanGridState = 'loading';
        const groupKey = `${domain}:${storefrontAccessToken}`;
        if (!groups.has(groupKey)) {
          groups.set(groupKey, { domain, storefrontAccessToken, cards: [] });
        }
        groups.get(groupKey).cards.push(card);
      });

      await Promise.all(
        Array.from(groups.values()).map(async ({ domain, storefrontAccessToken, cards: groupCards }) => {
          try {
            const productIds = [...new Set(groupCards.map((card) => card.dataset.productId))];
            const products = await fetchGridProducts(domain, storefrontAccessToken, productIds);
            groupCards.forEach((card) => {
              try {
                updateGridCard(card, products.get(card.dataset.productId));
              } catch (exception) {
                markGridCardUnavailable(card);
                console.error('Epicurean grid card could not be updated.', exception);
              }
            });
          } catch (exception) {
            groupCards.forEach(markGridCardUnavailable);
            console.error('Epicurean collection grid failed to load.', exception);
          }
        })
      );
    });
  }

  function observeGridChanges() {
    const observer = new MutationObserver((mutations) => {
      const hasNewGridCards = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('[data-epicurean-grid-card]') ||
              node.querySelector?.('[data-epicurean-grid-card]'))
        )
      );

      if (hasNewGridCards) queueGridUpdate();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        mountAll();
        queueGridUpdate();
        observeGridChanges();
      },
      { once: true }
    );
  } else {
    mountAll();
    queueGridUpdate();
    observeGridChanges();
  }

  document.addEventListener('shopify:section:load', (event) => {
    mountAll(event.target);
    queueGridUpdate();
  });
})();
